import request from '@/config/axios'

export interface Property {
  propertyId?: number // 属性编号
  propertyName?: string // 属性名称
  valueId?: number // 属性值编号
  valueName?: string // 属性值名称
}

export interface Sku {
  id?: number // 商品 SKU 编号
  name?: string // 商品 SKU 名称
  spuId?: number // SPU 编号
  properties?: Property[] // 属性数组
  price?: number | string // 商品价格
  marketPrice?: number | string // 市场价
  costPrice?: number | string // 成本价
  supplierSettlePrice?: number | string // 供货结算价（外发合同价）
  barCode?: string // 商品条码
  picUrl?: string // 图片地址
  stock?: number // 库存
  weight?: number // 商品重量，单位：kg 千克
  volume?: number // 商品体积，单位：m^3 平米
  firstBrokeragePrice?: number | string // 一级分销佣金(分) 历史字段
  secondBrokeragePrice?: number | string // 二级分销佣金(分) 历史字段
  firstBrokeragePercent?: number // 一级返佣比例 0-100（单独设置）
  secondBrokeragePercent?: number // 二级返佣比例 0-100（单独设置）
  salesCount?: number // 商品销量
}

export interface GiveCouponTemplate {
  id?: number
  name?: string // 优惠券名称
}

export interface Spu {
  id?: number
  name?: string // 商品名称
  categoryId?: number // 商品分类
  keyword?: string // 关键字
  unit?: number | undefined // 单位
  picUrl?: string // 商品封面图
  sliderPicUrls?: string[] // 商品轮播图
  introduction?: string // 商品简介
  deliveryTypes?: number[] // 配送方式
  deliveryTemplateId?: number | undefined // 运费模版
  brandId?: number // 商品品牌编号
  supplierId?: number // 供应商编号
  supplierName?: string // 供应商名称
  productSource?: number // 商品来源：1集采 2外发
  sourceChannel?: number // 货源渠道：1京东…
  sourceChannelName?: string // 货源渠道名称
  sourceUrl?: string // 货源链接
  specType?: boolean // 商品规格
  subCommissionType?: boolean // 分销类型
  areaAgentEnabled?: boolean // 是否参与区域代理分红（默认关）
  cashbackEnabled?: boolean // 是否开启商品返现（默认关）
  cashbackTotalPrice?: number // 单件总返现（分）
  cashbackDailyRate?: number // 日释放比例万分比（10%=1000）
  skus?: Sku[] // sku数组
  description?: string // 商品详情
  sort?: number // 商品排序
  giveIntegral?: number // 赠送积分
  virtualSalesCount?: number // 虚拟销量
  guaranteeIds?: number[] // 服务保障勾选（空=商详回落平台默认）
  price?: number // 商品价格
  combinationPrice?: number // 商品拼团价格
  seckillPrice?: number // 商品秒杀价格
  salesCount?: number // 商品销量
  marketPrice?: number // 市场价
  costPrice?: number // 成本价
  stock?: number // 商品库存
  createTime?: Date // 商品创建时间
  status?: number // 商品状态
  // B2B2C 审核字段
  shopId?: number // 所属店铺ID
  auditStatus?: number // 审核状态 0草稿 1审核中 2通过 3不通过 4已上架 5已下架
  auditRemark?: string // 审核备注
  auditUserId?: number // 审核人ID
  auditTime?: Date // 审核时间
}

// 获得 Spu 列表
export const getSpuPage = (params: PageParam) => {
  return request.get({ url: '/product/spu/page', params })
}

// 获得 Spu 列表 tabsCount
export const getTabsCount = () => {
  return request.get({ url: '/product/spu/get-count' })
}

// 创建商品 Spu
export const createSpu = (data: Spu) => {
  return request.post({ url: '/product/spu/create', data })
}

// 更新商品 Spu
export const updateSpu = (data: Spu) => {
  return request.put({ url: '/product/spu/update', data })
}

// 更新商品 Spu status
export const updateStatus = (data: { id: number; status: number }) => {
  return request.put({ url: '/product/spu/update-status', data })
}

/** 批量上架 / 下架；返回成功条数 */
export const updateStatusBatch = (data: { ids: number[]; status: number }) => {
  return request.put({ url: '/product/spu/update-status-batch', data })
}

// 获得商品 Spu
export const getSpu = (id: number) => {
  return request.get({ url: `/product/spu/get-detail?id=${id}` })
}

// 获得商品 Spu 详情列表
export const getSpuDetailList = (ids: number[]) => {
  return request.get({ url: `/product/spu/list?spuIds=${ids}` })
}

// 删除商品 Spu
export const deleteSpu = (id: number) => {
  return request.delete({ url: `/product/spu/delete?id=${id}` })
}

// 导出商品 Spu Excel
export const exportSpu = async (params: any) => {
  return await request.download({ url: '/product/spu/export-excel', params })
}

/** 导出可售清单（SKU） */
export const exportSaleableSpu = async (params: any) => {
  return await request.download({ url: '/product/spu/export-saleable-excel', params })
}

export interface ProductExcelImportFailure {
  rowIndex?: number
  bizKey?: string
  reason?: string
}

export interface ProductExcelImportResult {
  successCount: number
  failureCount: number
  failures?: ProductExcelImportFailure[]
}

/** 导入可售字段更新 */
export const importSaleableSpu = async (formData: FormData) => {
  const res = await request.upload({
    url: '/product/spu/import-saleable-excel',
    data: formData,
    timeout: 30 * 60 * 1000
  })
  return (res?.data ?? res) as ProductExcelImportResult
}

/** 下载新建商品导入模板 */
export const getCreateImportTemplate = async () => {
  return await request.download({ url: '/product/spu/get-create-import-template' })
}

/** 导入新建商品 */
export const importCreateSpu = async (formData: FormData) => {
  const res = await request.upload({
    url: '/product/spu/import-create-excel',
    data: formData,
    timeout: 30 * 60 * 1000
  })
  return (res?.data ?? res) as ProductExcelImportResult
}

// 获得商品 SPU 精简列表
export const getSpuSimpleList = async () => {
  return request.get({ url: '/product/spu/list-all-simple' })
}

// B2B2C 审核商品 SPU
export interface AuditReqVO {
  id: number
  auditStatus: number // 2=通过 3=不通过
  auditRemark?: string // 审核备注
}
export const auditSpu = (data: AuditReqVO) => {
  return request.put({ url: '/product/spu/audit', data })
}
